import {
  KitButton,
  KitIcon,
  KitText,
  KitTouchable,
} from '@omni/kit/components';
import {
  WindowSizeMin,
  useSizeClass,
} from '@omni/kit/contexts/SizeClassContext';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import { IGroupEvent } from '@omni/kit/services/GroupsService/Types';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import React, { ReactElement } from 'react';
import { Platform, View } from 'react-native';

interface AttendanceTypes {
  isAttendanceTaken: boolean;
  attendedCount?: number | undefined;
  onPressAttendance: (mode: 'create' | 'edit') => void;
  groupMembersLessThanFifty?: boolean;
  groupEvent?: IGroupEvent;
}

export default function EventAttendanceComponent({
  isAttendanceTaken,
  attendedCount,
  onPressAttendance,
  groupMembersLessThanFifty,
  groupEvent,
}: AttendanceTypes): ReactElement {
  const { t } = useTranslation(['groups']);
  const isWeb = Platform.OS === 'web';
  const { smallestWindowWidth, windowWidth } = useSizeClass();

  const isSmall = isWeb
    ? windowWidth < WindowSizeMin.L
    : smallestWindowWidth < WindowSizeMin.L;

  // If attendance is already taken, always use row layout
  const isRowLayout = isAttendanceTaken ? true : !isSmall;

  return (
    <View style={{ marginTop: Spacing.xxl }}>
      <View
        style={{
          flexDirection: isRowLayout ? 'row' : 'column',
          justifyContent: isRowLayout ? 'space-between' : 'flex-start',
          alignItems: isRowLayout ? 'center' : 'flex-start',
          gap: Spacing.m,
        }}
      >
        <View style={{ flex: 1, paddingRight: isRowLayout ? Spacing.m : 0 }}>
          <KitText
            fontSize={20}
            bold
            color={Colors.N900}
            style={{ marginBottom: Spacing.xs }}
          >
            {t('eventsAttendance')}
          </KitText>
          {groupEvent?._embedded?.sessions?.[0]?.note ? (
            <KitText fontSize={14} expandable numberOfLines={2}>
              {groupEvent?._embedded?.sessions?.[0]?.note}
            </KitText>
          ) : null}
          {!groupMembersLessThanFifty ? (
            <KitText fontSize={14} expandable numberOfLines={2}>
              {!isAttendanceTaken
                ? t('groups:noAttendanceSubmitted')
                : t('groups:peopleMarkedAsAttended', { count: attendedCount })}
            </KitText>
          ) : null}
        </View>

        {isAttendanceTaken ? (
          <KitTouchable
            testID='edit-attendance-button'
            onPress={() => onPressAttendance('edit')}
            style={{
              padding: 12,
              marginRight: 0,
              borderRadius: 22,
            }}
          >
            <KitIcon size={20} name='edit' />
          </KitTouchable>
        ) : (
          <KitButton
            testID='takeAttendance'
            secondary
            iconSize={14}
            title={t('groups:takeAttendanceButton')}
            onPress={() => onPressAttendance('create')}
            style={!isRowLayout ? { alignSelf: 'center' } : undefined}
          />
        )}
      </View>
    </View>
  );
}
